/**
 * Area `talk-rate` of the console gateway — over `apps/api/src/routes/talk-rate/` and `apps/api/src/routes/extra-time/`
 * (design §29.9.1, §29.11). Owned by plan-017 unit **WAPP-A** (wave 3): the organization's six talk rates, the upcoming
 * changes and the highest rate, read by the dashboard table, Billing, Plans, the organization overview and the low-credit
 * banner — every screen that shows "the rate that applies there" (OQ-1) — through one hook (`useTalkRates`) and one
 * formatter (`talkRateText`), design §29.17 IC-PR-11. And the organization's extra-time switch (owner answer OD-B, ADR
 * 0055), which Billing shows only where the terms in force include minutes (PQ-3).
 *
 * ```
 *   readTalkRates     `getTalkRates`    GET /v1/talk-rates          any member of the organization (PQ-7)
 *   readExtraTime     `getExtraTime`    GET /v1/account/extra-time  account.finance.read; ETag = the revision
 *   changeExtraTime   `putExtraTime`    PUT /v1/account/extra-time  account.finance.manage (PQ-2); If-Match required,
 *                                                                   409 revision_stale when someone changed it first
 *   readStandardTalkRates  `getPublicTalkRates`  GET /v1/public/talk-rates  public; the STANDARD rates in force
 * ```
 *
 * AMENDED 2026-10-17 (plan-020 AP2-FIX, owner decisions on the AP-2 review): `readStandardTalkRates` reads the standard
 * pay-as-you-go rates — what wetalk.io shows, never an organization's own or plan rate — for the way-in header's
 * Pricing line ("<rate> a minute, or less on a plan", design §32.12.2 A-1) and the "Pay as you go" card on
 * Finance → Plans. It is the api's public read (`standardTalkRatesAt`, the talk-rate-reader seam); nothing here reads
 * a rate any other way.
 *
 * EVERY FIGURE IS THE API'S. A kind with no price is absent from the answer and absent here — never defaulted — and the
 * hourly figure is the server's (`talk_rate_minor_per_hour`), never multiplied in the browser. The deprecated
 * `talk_rate_minor_per_minute` of `BillingBalance` (PQ-8) is not read by any WAPP-A screen.
 *
 * The register is EMPTY: all three operations are in the assembled spec. The sandbox half is `../sandbox/talk-rate.ts`.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { CURRENCY_CODE, money, TALK_RATE_KIND, TALK_RATE_SOURCE, type Money, type TalkRateKind, type TalkRateSource } from "@wetalk/domain";
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import { portalFormat, portalLocale, portalText } from "../../i18n/portal.js";
import { formatMoney, formatWholeMoney } from "../../money.js";
import type { ConsoleGateway } from "../gateway.js";
import { MalformedResponseError, readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import { useGateway } from "../provider.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

/** One kind's rate in force for the organization (`AccountTalkRate`). */
export interface TalkRateView {
  readonly kind: TalkRateKind;
  readonly perMinute: Money;
  /** The server's derived figure (× 60), never computed here. */
  readonly perHour: Money;
  /**
   * `account` — the organization's own price; `offer` — its plan's price (plan-020, design §32.2.6: the rate of the
   * plan the shared balance's terms name); `standard` — what everybody else pays. Shown, never computed with.
   */
  readonly source: TalkRateSource;
}

/** A scheduled change of one kind (`UpcomingTalkRateChange`); `null` on either side is "no price". */
export interface UpcomingTalkRateChangeView {
  readonly kind: TalkRateKind;
  readonly startsAt: Date;
  readonly from: Money | null;
  readonly to: Money | null;
  readonly toPerHour: Money | null;
  readonly source: TalkRateSource | null;
}

/** The organization's talk rates at one instant (`TalkRateSet`). */
export interface TalkRateSet {
  /** Every kind with a price now, in `TALK_RATE_KIND` order. */
  readonly talkRates: readonly TalkRateView[];
  /** Every scheduled change, earliest first (owner answer Q10). */
  readonly upcoming: readonly UpcomingTalkRateChangeView[];
  /** The highest rate in force — what the low-balance line and "minutes of credit" divide by (OD-C). */
  readonly highest: Money;
  readonly computedAt: Date;
}

/** The organization's extra-time switch (`ExtraTime`, ADR 0055). */
export interface ExtraTimeView {
  readonly enabled: boolean;
  /** What a change names in `If-Match`. */
  readonly revision: number;
  /** `false` under pay-as-you-go: the switch changes nothing there and is not shown (PQ-3). */
  readonly termsIncludeMinutes: boolean;
}

/** One kind's STANDARD rate in force (`PublicTalkRateSet` item): what everybody on pay as you go pays. */
export interface StandardTalkRateView {
  readonly kind: TalkRateKind;
  readonly perMinute: Money;
}

/** The standard talk rates in force (`PublicTalkRateSet`): every kind, in `TALK_RATE_KIND` order. */
export interface StandardTalkRateSet {
  readonly talkRates: readonly StandardTalkRateView[];
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** plan-017 WAPP-A: the organization's talk rates, the upcoming changes and the highest rate. */
    readTalkRates(): Promise<TalkRateSet>;
    /** plan-017 WAPP-A: the organization's extra-time switch (account.finance.read). */
    readExtraTime(): Promise<ExtraTimeView>;
    /**
     * plan-017 WAPP-A: turn extra time on or off (account.finance.manage), naming the revision read. Raises the API's
     * `409 revision_stale` when someone changed it first.
     */
    changeExtraTime(enabled: boolean, revision: number): Promise<ExtraTimeView>;
    /**
     * plan-020 AP2-FIX: the STANDARD talk rates in force (public, no credential needed) — the pay-as-you-go price
     * wetalk.io shows. Raises the api's `503 configuration_missing` while a kind has no standard rate.
     */
    readStandardTalkRates(): Promise<StandardTalkRateSet>;
  }
}

/** The methods this slice puts on `ConsoleGateway`. */
export type TalkRateGatewaySlice = Pick<ConsoleGateway, "readTalkRates" | "readExtraTime" | "changeExtraTime" | "readStandardTalkRates">;

/** This area's rows of `GATEWAY_GAP`: none — every operation is served. */
export const talkRateGap: readonly GatewayGap[] = [];

// ── the wire ─────────────────────────────────────────────────────────────────────────────────────────────

function kindOf(value: unknown, path: string): TalkRateKind {
  const raw = readText(value, path);
  const found = TALK_RATE_KIND.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a talk rate kind", raw);
  return found;
}

function sourceOf(value: unknown, path: string): TalkRateSource {
  const raw = readText(value, path);
  const found = TALK_RATE_SOURCE.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, TALK_RATE_SOURCE.join(", "), raw);
  return found;
}

function euro(value: unknown, path: string): Money {
  return money(readWholeNumber(value, path), CURRENCY_CODE);
}

function nullableEuro(value: unknown, path: string): Money | null {
  return value === null ? null : euro(value, path);
}

function currencyOf(row: Readonly<Record<string, unknown>>, path: string): void {
  const currency = readText(row["currency"], `${path}.currency`);
  if (currency !== CURRENCY_CODE) throw new MalformedResponseError(`${path}.currency`, CURRENCY_CODE, currency);
}

/** One `TalkRateSet`. */
export function talkRateSetOf(wire: unknown, path: string): TalkRateSet {
  const data = readRecord(wire, path);
  currencyOf(data, path);
  return {
    talkRates: readArray(data["talk_rates"], `${path}.talk_rates`).map((one, index) => {
      const at = `${path}.talk_rates[${String(index)}]`;
      const row = readRecord(one, at);
      currencyOf(row, at);
      return {
        kind: kindOf(row["talk_rate_kind"], `${at}.talk_rate_kind`),
        perMinute: euro(row["talk_rate_minor_per_minute"], `${at}.talk_rate_minor_per_minute`),
        perHour: euro(row["talk_rate_minor_per_hour"], `${at}.talk_rate_minor_per_hour`),
        source: sourceOf(row["source"], `${at}.source`),
      };
    }),
    upcoming: readArray(data["upcoming_talk_rate_change"], `${path}.upcoming_talk_rate_change`).map((one, index) => {
      const at = `${path}.upcoming_talk_rate_change[${String(index)}]`;
      const row = readRecord(one, at);
      currencyOf(row, at);
      return {
        kind: kindOf(row["talk_rate_kind"], `${at}.talk_rate_kind`),
        startsAt: new Date(readText(row["starts_at"], `${at}.starts_at`)),
        from: nullableEuro(row["previous_talk_rate_minor_per_minute"], `${at}.previous_talk_rate_minor_per_minute`),
        to: nullableEuro(row["talk_rate_minor_per_minute"], `${at}.talk_rate_minor_per_minute`),
        toPerHour: nullableEuro(row["talk_rate_minor_per_hour"], `${at}.talk_rate_minor_per_hour`),
        source: row["source"] === null ? null : sourceOf(row["source"], `${at}.source`),
      };
    }),
    highest: euro(data["highest_talk_rate_minor_per_minute"], `${path}.highest_talk_rate_minor_per_minute`),
    computedAt: new Date(readText(data["computed_at"], `${path}.computed_at`)),
  };
}

/** One `PublicTalkRateSet`: the per-minute figure of each kind (the hourly one is not shown anywhere it is read). */
export function standardTalkRateSetOf(wire: unknown, path: string): StandardTalkRateSet {
  const data = readRecord(wire, path);
  currencyOf(data, path);
  return {
    talkRates: readArray(data["talk_rates"], `${path}.talk_rates`).map((one, index) => {
      const at = `${path}.talk_rates[${String(index)}]`;
      const row = readRecord(one, at);
      currencyOf(row, at);
      return {
        kind: kindOf(row["talk_rate_kind"], `${at}.talk_rate_kind`),
        perMinute: euro(row["talk_rate_minor_per_minute"], `${at}.talk_rate_minor_per_minute`),
      };
    }),
  };
}

/** One `ExtraTime`. */
export function extraTimeOf(wire: unknown, path: string): ExtraTimeView {
  const data = readRecord(wire, path);
  return {
    enabled: readBoolean(data["extra_time_enabled"], `${path}.extra_time_enabled`),
    revision: readWholeNumber(data["extra_time_revision"], `${path}.extra_time_revision`),
    termsIncludeMinutes: readBoolean(data["terms_include_minutes"], `${path}.terms_include_minutes`),
  };
}

function dataOf(body: unknown, operationId: string): unknown {
  return readRecord(body, operationId)["data"];
}

export function talkRateApiSlice(client: WeTalkApiClient, _context: SliceContext): TalkRateGatewaySlice {
  return {
    async readTalkRates() {
      return talkRateSetOf(dataOf(assertOk(await client.getTalkRates()), "getTalkRates"), "getTalkRates.data");
    },
    async readExtraTime() {
      return extraTimeOf(dataOf(assertOk(await client.getExtraTime()), "getExtraTime"), "getExtraTime.data");
    },
    async changeExtraTime(enabled, revision) {
      const answer = await client.putExtraTime({
        body: { extra_time_enabled: enabled },
        header: { "if-match": `"${String(revision)}"` },
      });
      return extraTimeOf(dataOf(assertOk(answer), "putExtraTime"), "putExtraTime.data");
    },
    async readStandardTalkRates() {
      return standardTalkRateSetOf(dataOf(assertOk(await client.getPublicTalkRates()), "getPublicTalkRates"), "getPublicTalkRates.data");
    },
  };
}

// ── the reads screens share ──────────────────────────────────────────────────────────────────────────────

/** Query keys of this area — a prefix, so one invalidation reaches both. */
export const TALK_RATE_QUERY_KEY = {
  talkRate: ["talk-rate"] as const,
  extraTime: ["talk-rate", "extra-time"] as const,
  standard: ["talk-rate", "standard"] as const,
};

/**
 * THE hook: the organization's talk rates, read once and shared by every screen on the page. A refused or failed read
 * leaves `data` undefined — a screen then shows no figure, never a remembered one.
 */
export function useTalkRates(): UseQueryResult<TalkRateSet> {
  const gateway = useGateway();
  return useQuery({
    queryKey: TALK_RATE_QUERY_KEY.talkRate,
    queryFn: () => gateway.readTalkRates(),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * The STANDARD talk rates (plan-020 AP2-FIX), read once and shared: the way-in header's Pricing line and the "Pay as you
 * go" card. A refused or failed read leaves `data` undefined — the line and the card then show no figure.
 */
export function useStandardTalkRates(): UseQueryResult<StandardTalkRateSet> {
  const gateway = useGateway();
  return useQuery({
    queryKey: TALK_RATE_QUERY_KEY.standard,
    queryFn: () => gateway.readStandardTalkRates(),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * The pay-as-you-go figure a minute, as wetalk.io heads it (owner answer Q1): the one rate while every kind costs the
 * same, else the lowest, marked `from`. `null` while no kind is priced.
 */
export function standardTalkRateHeadlineOf(set: StandardTalkRateSet): { readonly perMinute: Money; readonly from: boolean } | null {
  const first = set.talkRates[0];
  if (first === undefined) return null;
  let low = first.perMinute;
  for (const each of set.talkRates) if (each.perMinute.amountMinor < low.amountMinor) low = each.perMinute;
  return { perMinute: low, from: set.talkRates.some((each) => !sameMoney(each.perMinute, first.perMinute)) };
}

// ── the words ────────────────────────────────────────────────────────────────────────────────────────────

/** "<rate> a minute" / "<rate> ανά λεπτό"; "<rate> an hour" / "<rate> ανά ώρα" — from the rate, never from a literal. */
export function talkRateText(rate: Money, unit: "minute" | "hour"): string {
  return unit === "minute"
    ? portalText("core", "talk_rate.per_minute", { value0: formatMoney(rate) })
    : portalText("core", "talk_rate.per_hour", { value0: formatWholeMoney(rate) });
}

/** The glossary's customer copy for a kind (glossary §6.28; `packages/domain/src/billing/talk-rate.ts`). */
export function talkRateKindLabel(kind: TalkRateKind): string {
  switch (kind) {
    case "carrier_leg_inbound":
      return portalText("core", "talk_rate.kind.carrier_leg_inbound");
    case "carrier_leg_outbound":
      return portalText("core", "talk_rate.kind.carrier_leg_outbound");
    case "sip_trunk_inbound":
      return portalText("core", "talk_rate.kind.sip_trunk_inbound");
    case "sip_trunk_outbound":
      return portalText("core", "talk_rate.kind.sip_trunk_outbound");
    case "browser":
      return portalText("core", "talk_rate.kind.browser");
    case "text":
      return portalText("core", "talk_rate.kind.text");
  }
}

/** One line of a rate table: one kind, or every kind at once while their rates are equal. */
export interface TalkRateLine {
  /** The kinds this line prices — all of them on the one-figure line. */
  readonly kind: readonly TalkRateKind[];
  readonly label: string;
  readonly perMinute: Money;
  readonly perHour: Money;
  /**
   * Whose price the line is: `account` when every kind on it is the organization's own rate, `offer` when every kind
   * is its plan's rate, else `standard` (plan-020 PIN; before plan-020 a mixed one-figure line said "Standard price",
   * and it still does while no kind on it is a plan's rate).
   */
  readonly source: TalkRateSource;
}

function sameMoney(left: Money, right: Money): boolean {
  return left.amountMinor === right.amountMinor && left.currency === right.currency;
}

/**
 * Owner answer Q1 as the portal draws it: one line while every kind has the same rate, else one line per kind, in
 * `TALK_RATE_KIND` order. Empty when no kind has a price (the screen then says so rather than show a figure).
 *
 * plan-020 PIN: a one-figure line names one source. When every kind shares it, that is the line's; when they differ
 * and none is a plan's rate, the line says "Standard price" exactly as before; when a plan's rate is mixed with
 * another source, the kinds are listed one per line, so no kind is labelled with a source it does not have.
 */
export function talkRateLinesOf(rates: readonly TalkRateView[]): readonly TalkRateLine[] {
  const first = rates[0];
  if (first === undefined) return [];
  const oneSource = rates.every((one) => one.source === first.source);
  const anyOffer = rates.some((one) => one.source === "offer");
  if (rates.every((one) => sameMoney(one.perMinute, first.perMinute)) && (oneSource || !anyOffer)) {
    return [
      {
        kind: rates.map((one) => one.kind),
        label: portalText("core", "talk_rate.every_kind"),
        perMinute: first.perMinute,
        perHour: first.perHour,
        source: oneSource ? first.source : "standard",
      },
    ];
  }
  return rates.map((one) => ({
    kind: [one.kind],
    label: talkRateKindLabel(one.kind),
    perMinute: one.perMinute,
    perHour: one.perHour,
    source: one.source,
  }));
}

/** The one figure every kind shares, or `null` while they differ (or none is priced). */
export function oneTalkRateOf(rates: readonly TalkRateView[]): TalkRateView | null {
  const first = rates[0];
  if (first === undefined) return null;
  return rates.every((one) => sameMoney(one.perMinute, first.perMinute)) ? first : null;
}

/** "€0.40–€0.60 a minute, by kind of conversation" — the short form where one line per kind does not fit. */
export function talkRateRangeText(rates: readonly TalkRateView[]): string | null {
  const one = oneTalkRateOf(rates);
  if (one !== null) return talkRateText(one.perMinute, "minute");
  const first = rates[0];
  if (first === undefined) return null;
  let low = first.perMinute;
  let high = first.perMinute;
  for (const each of rates) {
    if (each.perMinute.amountMinor < low.amountMinor) low = each.perMinute;
    if (each.perMinute.amountMinor > high.amountMinor) high = each.perMinute;
  }
  return portalText("core", "talk_rate.range", { value0: formatMoney(low), value1: formatMoney(high) });
}

/** "Calls to your WeTalk number: <new rate> a minute from 1 December 2026 (now <old rate>)." */
export function upcomingTalkRateChangeText(change: UpcomingTalkRateChangeView): string {
  const date = portalFormat(portalLocale()).date(change.startsAt, { day: "numeric", month: "long", year: "numeric" });
  const kind = talkRateKindLabel(change.kind);
  if (change.to === null) return portalText("core", "talk_rate.upcoming_none", { value0: kind, value1: date });
  if (change.from === null) {
    return portalText("core", "talk_rate.upcoming_new", { value0: kind, value1: talkRateText(change.to, "minute"), value2: date });
  }
  return portalText("core", "talk_rate.upcoming", {
    value0: kind,
    value1: talkRateText(change.to, "minute"),
    value2: date,
    value3: formatMoney(change.from),
  });
}

/** Whole minutes of talk `available` buys at `rate`; `null` for a free rate (no limit to state). */
export function talkMinuteAffordableOf(available: Money, rate: Money): number | null {
  if (rate.amountMinor <= 0) return null;
  return Math.max(0, Math.floor(available.amountMinor / rate.amountMinor));
}
